/**
 * Safe telemetry client.
 *
 * Fire-and-forget. Never blocks the user's flow, never throws upward.
 * Schema and forbidden fields per AI_CODING_IMPLEMENTATION_BRIEF.md §15
 * and PRODUCT_ROADMAP.md §14. The server-side Pydantic model enforces the
 * field allowlist; this module's job is to enrich the payload with
 * always-known context (app version, Excel platform, plan code) and post.
 */
import { postJson } from "./outbound";
import {
  APP_VERSION,
  getExcelPlatform,
  getOfficeVersionBucket,
} from "./clientContext";
import { pushTelemetry } from "./diagnosticsBuffer";

const SERVER_URL = process.env.EXTRACT_SERVER_URL.replace(/\/+$/, "");

/**
 * Fields a caller may set. Anything outside this list is silently dropped
 * before send — telemetry should never accidentally exfiltrate data.
 *
 * The bigger safety net is on the server: a Pydantic `extra="forbid"`
 * model 422s any unknown field, so a bug here can't sneak workbook
 * content past the boundary either.
 */
export interface TelemetryEvent {
  event_name: string;
  feature_code?: string;
  duration_ms?: number;
  workbook_size_bucket?: string;
  worksheet_count_bucket?: string;
  formula_count_bucket?: string;
  error_code?: string;
}

interface EnrichedTelemetryEvent extends TelemetryEvent {
  app_version: string;
  excel_platform: string;
  office_version_bucket: string;
  plan_code: string;
  contains_workbook_data: false;
}

function enrich(event: TelemetryEvent): EnrichedTelemetryEvent {
  return {
    ...event,
    app_version: APP_VERSION,
    excel_platform: getExcelPlatform(),
    office_version_bucket: getOfficeVersionBucket(),
    plan_code: "v0_tester",
    contains_workbook_data: false,
  };
}

/**
 * Post a safe telemetry event. Errors are swallowed silently — telemetry
 * must never break the user's flow or surface as visible UI failure.
 */
export function emitEvent(event: TelemetryEvent): void {
  // Local ring buffer first — the diagnostics packet reads from this and
  // we want the event captured even when the server post fails (offline,
  // signed out, server down).
  pushTelemetry(event);

  const payload = enrich(event);
  void postJson(`${SERVER_URL}/api/telemetry/event`, payload).catch((err) => {
    // Log to console for debugging during dev; in prod this becomes
    // invisible noise the user never sees.
    console.warn("telemetry: emit failed", event.event_name, err);
  });
}
